import { isNotNil } from "@vendrinc/frontend-ts";
import * as Zod from "zod";
import {
  initialQuestionToLabel,
  questionToLabel,
  Question,
  InitialQuestion,
} from "./questions";
import * as BackofficeClient from "../backoffice/client";

export enum HowSoon {
  Asap = "ASAP",
  Within30Days = "Within 30 days",
  Within90Days = "Within 90 days",
  Within6Months = "Within 6 months",
  Undecided = "Undecided",
}

export enum SupplierContact {
  NoContact = "No contact",
  ContactedNoQuote = "Contacted, no quote",
  ContactedWithQuote = "Contacted with quote",
}

export enum TypeOfPurchase {
  NewPurchase = "New Purchase",
  Renewal = "Renewal",
  MidContractAmendment = "Mid-Contract Amendment",
}

export const typeOfPurchaseDecoder: Zod.ZodNativeEnum<typeof TypeOfPurchase> =
  Zod.nativeEnum(TypeOfPurchase);

export const howSoonDecoder: Zod.ZodNativeEnum<typeof HowSoon> =
  Zod.nativeEnum(HowSoon);

export const areYouEvaluatingCompetitorsDecoder = Zod.discriminatedUnion(
  "type",
  [
    Zod.object({ type: Zod.literal("no") }),
    Zod.object({
      type: Zod.literal("yes"),
      products: Zod.array(
        Zod.object({
          id: Zod.string(),
          name: Zod.string(),
          slug: Zod.string(),
        }),
      ),
    }),
  ],
);

export const compareOptionsToMaximizeSavingsDecoder = Zod.object({
  other: Zod.boolean(),
  products: Zod.array(
    Zod.object({
      id: Zod.string(),
      name: Zod.string(),
      slug: Zod.string(),
      logo: Zod.string().optional(),
      isVendrVerified: Zod.boolean().optional(),
    }),
  ),
});

export const addOnDecoder = Zod.object({
  id: Zod.string(),
  name: Zod.string(),
  description: Zod.string().nullable(),
  tierId: Zod.string().nullable(),
});

export type AddOn = Zod.infer<typeof addOnDecoder>;

export type AreYouEvaluatingCompetitors = Zod.infer<
  typeof areYouEvaluatingCompetitorsDecoder
>;

export type CompareOptionsToMaximizeSavings = Zod.infer<
  typeof compareOptionsToMaximizeSavingsDecoder
>;

export enum WhereAreYouInPurchaseCycle {
  InEarlyConversations = "I am in early conversations",
  ReceivedAQuote = "I have received an initial quote",
  CloseToFinalizing = "I have finalized terms and pricing",
}

export const whereAreYouInPurchaseCycleDecoder: Zod.ZodNativeEnum<
  typeof WhereAreYouInPurchaseCycle
> = Zod.nativeEnum(WhereAreYouInPurchaseCycle);

export enum TermLength {
  OneYear = "One-year term",
  TwoYear = "Two-year term",
  ThreeYear = "Three-year term",
}

export enum ScopeChange {
  NoScopeChange = "No scope change",
  Downgrade = "Downgrade",
  Upgrade = "Upgrade",
}

export const termLengthDecoder: Zod.ZodNativeEnum<typeof TermLength> =
  Zod.nativeEnum(TermLength);

export enum BillingFrequency {
  Quarterly = "Quarterly",
  SemiAnnual = "Semi-annual",
  Annual = "Annual",
}

export const billingFrequencyDecoder: Zod.ZodNativeEnum<
  typeof BillingFrequency
> = Zod.nativeEnum(BillingFrequency);

export const areYouReplacingExistingSolutionDecoder = Zod.discriminatedUnion(
  "type",
  [
    Zod.object({ type: Zod.literal("no") }),
    Zod.object({
      type: Zod.literal("yes"),
      product: Zod.object({
        id: Zod.string(),
        name: Zod.string(),
        slug: Zod.string(),
        category: Zod.string(),
      })
        .optional()
        .nullable(),
    }),
  ],
);

export type AreYouReplacingExistingSolution = Zod.infer<
  typeof areYouReplacingExistingSolutionDecoder
>;

export const requestTypeDecoder = Zod.union([
  Zod.literal("quote"),
  Zod.literal("price_check"),
  Zod.literal("price_estimate"),
  Zod.literal("price_estimate_v2"),
]);

export type RequestType = Zod.infer<typeof requestTypeDecoder>;

export const attachmentDecoder = Zod.object({
  filename: Zod.string(),
  mimetype: Zod.string(),
  backofficeDocumentId: Zod.string().uuid(),
  backofficeDocumentReferenceId: Zod.string().uuid().optional(),
});

export type Attachment = Zod.infer<typeof attachmentDecoder>;

export const requestTypeLockDecoder = Zod.object({
  requestType: requestTypeDecoder,
  typeOfPurchaseSpecified: Zod.boolean().nullable(),
});

export type RequestTypeLock = Zod.infer<typeof requestTypeLockDecoder>;

export const scopeTermsDecoder = Zod.object({
  termLength: Zod.number(),
  purchaseType: Zod.enum(["new_purchase", "renewal"]),
  autoRenew: Zod.boolean().nullable().optional(),
  startDate: Zod.string().nullable().optional(),
  endDate: Zod.string().nullable().optional(),
  listPrice: Zod.number().nullable().optional(),
  discount: Zod.number().nullable().optional(),
  finalPrice: Zod.number().nullable().optional(),
});

export const priceEstimateDecoder = Zod.object({
  scopeTerms: scopeTermsDecoder,
  pricingDimensions: Zod.record(Zod.string(), Zod.number().nullable()),
});

export type PriceEstimate = Zod.infer<typeof priceEstimateDecoder>;

export const initialAnswersDecoder = Zod.object({
  companyId: Zod.string(),
  productId: Zod.string().nullable(),
  tierId: Zod.string().optional().nullable(),
  tierName: Zod.string().optional().nullable(),
  haveYouSpokenWith: Zod.boolean().nullable(),
  typeOfPurchase: Zod.nativeEnum(TypeOfPurchase).nullable(),
  howSoonToBuy: howSoonDecoder.nullable(),
});

export type InitialAnswers = Zod.infer<typeof initialAnswersDecoder>;

export const answersDecoder = initialAnswersDecoder.and(
  Zod.object({
    requestTypeLock: requestTypeLockDecoder.optional(),

    compareOptionsToMaximizeSavings: compareOptionsToMaximizeSavingsDecoder
      .optional()
      .nullable(),
    tellUsMoreAboutYourPurchase: Zod.string().optional().nullable(),
    anythingElseWeNeedToKnow: Zod.string().optional().nullable(),
    whereAreYouInPurchaseCycle: whereAreYouInPurchaseCycleDecoder
      .optional()
      .nullable(),
    areYouReplacingExistingSolution: areYouReplacingExistingSolutionDecoder
      .optional()
      .nullable(),
    quantity: Zod.number().nullish(),
    termLength: termLengthDecoder.optional().nullable(),
    billingFrequency: billingFrequencyDecoder.optional().nullable(),
    viaAwsMarketplace: Zod.boolean().optional().nullable(),
    acceptVendrVerifiedBenefits: Zod.boolean().optional().nullable(),
    addOns: Zod.array(addOnDecoder).nullish(),
    scopeChange: Zod.nativeEnum(ScopeChange).optional().nullable(),
    scopeAdded: Zod.string().optional().nullable(),
    scopeRemoved: Zod.string().optional().nullable(),
    haveRenewalQuote: Zod.boolean().optional().nullable(),
    quotes: Zod.array(attachmentDecoder).nullish(),
    previousContracts: Zod.array(attachmentDecoder).nullish(),
    openToMultiyearContract: Zod.boolean().optional().nullable(),
    priceEstimate: priceEstimateDecoder.optional().nullable(),

    // NOTE These fields are actually derived data, but needed to prevent submission of form
    // and we do not want to start customizing submission logic to price estimates only. Answers
    // abstraction doesn't quite fit but its okay for now
    skuId: Zod.string().nullish(),
    billingUnits: Zod.string().nullish(),
  }),
);

export type Answers = Zod.infer<typeof answersDecoder>;

export type FileUploads = {
  quotes: File[];
  previousContracts: File[];
};

export type CommonAnswerType =
  | { question: string; textAnswer: string }
  | { question: string; boolAnswer: boolean };

export const encodeCommonAnswers = (answers: Answers): CommonAnswerType[] => {
  const supplier = { name: "Supplier" };
  const toLabel = questionToLabel(supplier);
  const initialToLabel = initialQuestionToLabel(supplier);

  const encodedAnswers: CommonAnswerType[][] = [
    answers.acceptVendrVerifiedBenefits !== null &&
    answers.acceptVendrVerifiedBenefits !== undefined
      ? [
          {
            question: "Accepts Vendr Verified benefits",
            boolAnswer: answers.acceptVendrVerifiedBenefits,
          },
        ]
      : [],
    answers.tierName !== null && answers.tierName !== undefined
      ? [
          {
            question: initialToLabel(InitialQuestion.Tier),
            textAnswer: answers.tierName,
          },
        ]
      : [],
    answers.howSoonToBuy !== null
      ? [
          {
            question: initialToLabel(InitialQuestion.HowSoon),
            textAnswer: answers.howSoonToBuy,
          },
        ]
      : [],

    answers.haveYouSpokenWith !== null
      ? [
          {
            question: initialToLabel(InitialQuestion.HaveYouSpoken),
            boolAnswer: answers.haveYouSpokenWith,
          },
        ]
      : [],

    answers.compareOptionsToMaximizeSavings !== null &&
    answers.compareOptionsToMaximizeSavings !== undefined &&
    answers.compareOptionsToMaximizeSavings.products.length > 0
      ? [
          {
            question: toLabel(Question.CompareOptionsToMaximizeSavings),
            textAnswer: (() => {
              return answers
                .compareOptionsToMaximizeSavings!.products.map((p) => p.name)
                .join(", ");
            })(),
          },
        ]
      : [],

    answers.areYouReplacingExistingSolution !== null &&
    answers.areYouReplacingExistingSolution !== undefined
      ? [
          {
            question: toLabel(Question.AreYouReplacingExistingSolution),
            textAnswer: (() => {
              if (answers.areYouReplacingExistingSolution.type === "yes") {
                return `Yes: ${answers.areYouReplacingExistingSolution.product?.name}`;
              }

              return "No";
            })(),
          },
        ]
      : [],
    answers.whereAreYouInPurchaseCycle !== null &&
    answers.whereAreYouInPurchaseCycle !== undefined
      ? [
          {
            question: toLabel(Question.WhereAreYouInPurchaseCycle),
            textAnswer: answers.whereAreYouInPurchaseCycle,
          },
        ]
      : [],

    answers.typeOfPurchase !== null && answers.typeOfPurchase !== undefined
      ? [
          {
            question: "Purchase type",
            textAnswer: answers.typeOfPurchase,
          },
        ]
      : [],

    answers.scopeAdded !== null && answers.scopeAdded !== undefined
      ? [
          {
            question: toLabel(Question.ScopeAdded),
            textAnswer: answers.scopeAdded,
          },
        ]
      : [],

    answers.scopeRemoved !== null && answers.scopeRemoved !== undefined
      ? [
          {
            question: toLabel(Question.ScopeRemoval),
            textAnswer: answers.scopeRemoved,
          },
        ]
      : [],
    answers.scopeChange !== null && answers.scopeChange !== undefined
      ? [
          {
            question: toLabel(Question.ScopeChange),
            textAnswer: answers.scopeChange,
          },
        ]
      : [],
    answers.tellUsMoreAboutYourPurchase !== null &&
    answers.tellUsMoreAboutYourPurchase !== undefined
      ? [
          {
            question: toLabel(Question.TellUsMoreAboutYourPurchase),
            textAnswer: answers.tellUsMoreAboutYourPurchase,
          },
        ]
      : [],
    answers.quantity !== null && answers.quantity !== undefined
      ? [
          {
            question: "Number of units",
            textAnswer: answers.quantity.toString(),
          },
        ]
      : [],
    answers.termLength !== null && answers.termLength !== undefined
      ? [
          {
            question: "Term length",
            textAnswer: answers.termLength,
          },
        ]
      : [],
    answers.billingFrequency !== null && answers.billingFrequency !== undefined
      ? [
          {
            question: "Billing frequency",
            textAnswer: answers.billingFrequency,
          },
        ]
      : [],
    answers.addOns !== null && answers.addOns !== undefined
      ? [
          {
            question: toLabel(Question.AddOns),
            textAnswer: answers.addOns.map((x) => x.name).join(", "),
          },
        ]
      : [],
    answers.anythingElseWeNeedToKnow !== null &&
    answers.anythingElseWeNeedToKnow !== undefined
      ? [
          {
            question: toLabel(Question.AnythingElseWeNeedToKnow),
            textAnswer: answers.anythingElseWeNeedToKnow,
          },
        ]
      : [],

    answers.viaAwsMarketplace !== null &&
    answers.viaAwsMarketplace !== undefined
      ? [
          {
            question: toLabel(Question.ViaAwsMarketplace),
            boolAnswer: answers.viaAwsMarketplace,
          },
        ]
      : [],
    answers.haveRenewalQuote !== null && answers.haveRenewalQuote !== undefined
      ? [
          {
            question: toLabel(Question.HaveRenewalQuote),
            boolAnswer: answers.haveRenewalQuote,
          },
        ]
      : [],

    isNotNil(answers.openToMultiyearContract)
      ? [
          {
            question: "Would you be open to multi year contract?",
            boolAnswer: answers.openToMultiyearContract,
          },
        ]
      : [],
  ];

  return encodedAnswers.flatMap((i) => i);
};

export const getRequestType = (answers: Partial<Answers>): RequestType => {
  if (answers.haveYouSpokenWith === false) {
    return "quote";
  } else if (
    answers.whereAreYouInPurchaseCycle ===
    WhereAreYouInPurchaseCycle.InEarlyConversations
  ) {
    return "quote";
  } else {
    return "price_check";
  }
};

// Slightly new logic as to how we determine the request type
// If they have a quote, then we're gonna check it
// If they dont have a quote, then we're gonna get them a quote (private offer)
export const getNewRequestType = (
  answers: Partial<Answers>,
  company: Pick<BackofficeClient.Company, "isVendrVerified">,
): RequestType => {
  // Once we're locked into a request flow don't allow
  // changes to the request type based on answers on the
  // "remaining" request screens (ie. non-listing page)
  // Or if we're designated a request type from the url param linked from another page
  if (answers.requestTypeLock) {
    return answers.requestTypeLock.requestType;
  }

  if (answers.haveRenewalQuote === true) {
    return "price_check";
  } else if (
    answers.typeOfPurchase === TypeOfPurchase.NewPurchase &&
    company.isVendrVerified
  ) {
    return "quote";
  } else {
    return "price_estimate";
  }
};

export const instantPrivateOfferAnswersDecoder = Zod.object({
  productId: Zod.string(),
  tierId: Zod.string().optional().nullable(),
  tierName: Zod.string().optional().nullable(),
  quantity: Zod.number().nullish(),
  termLength: termLengthDecoder.optional().nullable(),
  billingFrequency: billingFrequencyDecoder.optional().nullable(),
  addOns: Zod.array(addOnDecoder).nullish(),
});

export type InstantPrivateOfferAnswers = Zod.infer<
  typeof instantPrivateOfferAnswersDecoder
>;
